import { css, DefaultTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";

/**
 * The new look's design roles, read from the theme. Surfaces: a tinted canvas,
 * panels on it with a hairline (no shadow), inset wells inside panels. Text:
 * ink for titles and figures, a firm grey for labels, a lighter one for meta.
 * Colour carries meaning only: brand blue for interaction and selection,
 * green/red for direction, orange for stops.
 */
export const getNewLookTokens = (theme: DefaultTheme) => {
  const dark = theme.isDarkMode;
  const { colors } = theme;
  return {
    canvas: colors.canvasBgColor,
    panel: colors.cardBgColor,
    well: dark ? hexToRGB("#FFFFFF", 0.03) : colors.cardLightBgColor,
    hairline: colors.cardBorderColor,
    hairlineStrong: colors.lightBorderGrey,
    rowHover: dark ? hexToRGB("#FFFFFF", 0.035) : colors.tableHoverBgColor,
    ink: colors.titleColor,
    text: colors.primaryText,
    secondary: colors.secondaryText,
    muted: colors.mutedText,
    accent: colors.primaryBlue,
    up: colors.green,
    down: colors.red,
    stop: colors.orange,
    focus: hexToRGB(colors.primaryBlue, 0.45),
  };
};

export type NewLookTokens = ReturnType<typeof getNewLookTokens>;

/** Panels 12, controls 8, chips 6. */
export const NEW_LOOK_RADIUS = { panel: 12, control: 8, chip: 6 };
/** Space between panels on a page. */
export const NEW_LOOK_GAP = 16;

/**
 * Typography roles: one size and weight per role, tabular figures. Sizes and
 * weights stay on the calm side (Inter reads denser than the classic Nunito
 * Sans), using Inter's variable in-between weights.
 */
export const newLookType = {
  /** The one hero figure on a page (portfolio value). */
  display: css`
    font-size: 36px;
    line-height: 40px;
    font-weight: 560;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
  `,
  /** Page titles inside the content (not the header's page title). */
  pageTitle: css`
    font-size: 19px;
    line-height: 26px;
    font-weight: 520;
    letter-spacing: -0.01em;
  `,
  panelTitle: css`
    font-size: 15px;
    line-height: 22px;
    font-weight: 520;
    letter-spacing: -0.01em;
  `,
  figure: css`
    font-size: 14.5px;
    line-height: 20px;
    font-weight: 460;
    font-variant-numeric: tabular-nums;
  `,
  figureLarge: css`
    font-size: 19px;
    line-height: 26px;
    font-weight: 500;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
  `,
  body: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  `,
  label: css`
    font-size: 12.5px;
    line-height: 16px;
    font-weight: 450;
  `,
  meta: css`
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    font-variant-numeric: tabular-nums;
  `,
  /** Column headers and small section labels. */
  micro: css`
    font-size: 11px;
    line-height: 14px;
    font-weight: 520;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  `,
};

/** Max width of a new-look page's content on wide screens. */
export const NEW_LOOK_CONTENT_MAX = 1680;

/**
 * The one hero surface on a page: the panel with a soft brand tint washing in
 * from the top left, so it reads first without any decoration.
 */
export const newLookHero = css`
  background: radial-gradient(
      120% 140% at 0% 0%,
      ${(p) =>
        hexToRGB(p.theme.colors.primaryBlue, p.theme.isDarkMode ? 0.16 : 0.06)},
      transparent 60%
    ),
    ${(p) => p.theme.colors.cardBgColor};
`;

export const NEW_LOOK_ON_BRAND = {
  text: "#FFFFFF",
  secondary: "rgba(255, 255, 255, 0.8)",
  muted: "rgba(255, 255, 255, 0.62)",
  well: "rgba(255, 255, 255, 0.18)",
  grid: "rgba(255, 255, 255, 0.14)",
  ticks: "rgba(255, 255, 255, 0.42)",
  up: "#79FF9F",
  down: "#FF8A8A",
};

export const newLookBrandHero = css`
  color: ${NEW_LOOK_ON_BRAND.text};
  background: ${(p) =>
    p.theme.isDarkMode
      ? "linear-gradient(160deg, #1E3A8A 0%, #1B3173 45%, #172554 100%)"
      : `linear-gradient(160deg, #3D72E6 0%, ${p.theme.colors.primaryBlue} 45%, #2A52B8 100%)`};
  box-shadow: ${(p) =>
    p.theme.isDarkMode ? "none" : "0 12px 28px -18px rgba(42, 82, 184, 0.6)"};
`;

/** Direction colour for a signed value (ink at zero). */
export const directionColor = (tokens: NewLookTokens, value?: number) => {
  if (!value) return tokens.ink;
  return value > 0 ? tokens.up : tokens.down;
};

/**
 * The edge of a card surface as a box-shadow: the soft shadow in light mode
 * (cards carry no border there), a faint 1px hairline in dark mode where
 * shadows do not read.
 */
export const getPanelShadow = (theme: DefaultTheme) =>
  theme.isDarkMode
    ? `inset 0 0 0 1px ${theme.colors.cardBorderColor}`
    : `inset 0 0 0 1px ${theme.colors.cardBorderColor}, ${theme.shadows.panel}`;

/**
 * Figures (amounts, prices, percents) sit a step lighter than the names and
 * titles beside them (500): size and ink carry them, so a card reads as a
 * name with its numbers rather than as all bold.
 */
export const NEW_LOOK_FIGURE_WEIGHT = 450;

/**
 * The phone type ramp (new look). One size and weight per role so pages stop
 * picking their own in-between sizes; calmer than the desktop scale.
 */
export const newLookPhoneType = {
  /** The one hero figure on a phone page. */
  hero: css`
    font-size: clamp(28px, 8.5vw, 34px);
    line-height: 1.15;
    font-weight: 520;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
  `,
  /** Section titles ("Open Trades", "Accounts"). */
  section: css`
    font-size: 17px;
    line-height: 24px;
    font-weight: 500;
    letter-spacing: -0.01em;
  `,
  /** The page title in the phone header. */
  headerTitle: css`
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
    letter-spacing: -0.01em;
  `,
  /** Card titles and row names (bot, account, pair). */
  cardTitle: css`
    font-size: 15px;
    line-height: 20px;
    font-weight: 500;
  `,
  /** Figures inside cards and tiles (PnL, prices, balances). */
  figure: css`
    font-size: 16px;
    line-height: 22px;
    font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
    font-variant-numeric: tabular-nums;
  `,
  /** A row's lead figure, beside its card title (PnL, balance). */
  rowFigure: css`
    font-size: 15px;
    line-height: 20px;
    font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
    font-variant-numeric: tabular-nums;
  `,
  body: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  `,
  /** Tab labels (the active tab adds weight 500). */
  tab: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: 450;
  `,
  /** Field and stat labels, in the secondary grey. */
  label: css`
    font-size: 12.5px;
    line-height: 16px;
    font-weight: 400;
  `,
  /** Meta lines (times, counts, sub lines), in the muted grey. */
  meta: css`
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    font-variant-numeric: tabular-nums;
  `,
  /** Bottom navigation labels: the smallest readable size. */
  nav: css`
    font-size: 11px;
    line-height: 14px;
    font-weight: 500;
  `,
};
